import * as React from 'react';
import { Check, Copy } from 'lucide-react';

import { copyToClipboard } from '@klareospace/pharos-lib-front-utils';

import { cn } from '../../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';

// ─── Constants ────────────────────────────────────────────────────────────────

const COPIED_DISPLAY_MS = 3000;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InlineCopyTextProps {
  /** The text content to display — rendered as an inline heading */
  children: React.ReactNode;
  /** Additional CSS classes on the wrapper span */
  className?: string;
  /** Tooltip label shown while the green check is visible — i18n provided by caller */
  copiedTooltip: string;
  /** Tooltip label shown before copying — i18n provided by caller */
  copyTooltip: string;
  /** The text value to copy to the clipboard */
  value: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Inline text with a trailing copy icon.
 *
 * - Clicking copies `value` to the clipboard.
 * - The copy icon swaps to a green check for 3 seconds, then reverts.
 * - Tooltip is i18n-agnostic — pass already-translated strings.
 * - `CopyableField` cannot serve this use-case: it renders as a block form
 *   input with a toast notification, not as an inline heading with a transient
 *   green-check state.
 * - Sized to its content (`w-fit`) rather than stretching: when a caller
 *   places this inside a `flex flex-col` container, the default cross-axis
 *   `align-items: stretch` would otherwise widen the trigger/tooltip region
 *   to the full container width, making the copy tooltip appear when
 *   hovering empty space far past the visible text/icon. `w-fit` keeps the
 *   hover/click region matched to the visible content in any flex context.
 */
export function InlineCopyText({
  children,
  className,
  copiedTooltip,
  copyTooltip,
  value,
}: InlineCopyTextProps): React.JSX.Element {
  const [copied, setCopied] = React.useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount
  React.useEffect(() => {
    return (): void => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  function handleCopy(): void {
    void copyToClipboard(value).then((success) => {
      if (success) {
        setCopied(true);
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          setCopied(false);
          timeoutRef.current = null;
        }, COPIED_DISPLAY_MS);
      }
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLSpanElement>): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCopy();
    }
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn('inline-flex w-fit items-center gap-2 cursor-pointer group/copy relative', className)}
            onClick={handleCopy}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
          >
            {children}
            {copied ? (
              <Check className="w-4 h-4 text-success shrink-0" data-testid="icon-check" />
            ) : (
              <Copy className="w-4 h-4 text-muted-foreground shrink-0" data-testid="icon-copy" />
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <span className="text-xs">{copied ? copiedTooltip : copyTooltip}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
